import { Store, GetterTree, MutationTree, ActionTree, Module } from "vuex";

import { RootState } from "..";
import { IPropertyOverviewState } from "../states/property-overview-state";
import container from "../../inversify.config";
import PerformanceService from "../../services/performance.service";
import EnquiryService from "../../services/enquiry.service";
import { IPropertyPerformanceSummary } from "../../models/interfaces/performance/property-performance-summary.model.interface";
import { IDailyPerformanceSummary } from "../../models/interfaces/performance/daily-performance-summary.model.interface";
import { IPropertyEnquirySummary } from "../../models/interfaces/enquiry/property-enquiry-summary.model.interface";
import { IPropertyOverview } from "../../models/interfaces/overview/property-overview.model.interface";

export const state: IPropertyOverviewState = {
  propertiesPerformance: [],
  propertiesDailyPerformance : {
    keys: [],
    loads: [],
    impressions: [],
    clicks: [],
    keyPresses: []
  },
  propertiesEnquiries: []
};

export const getters: GetterTree<IPropertyOverviewState, RootState> = {
  propertiesPerformance(state) {
    return state.propertiesPerformance;
  },
  propertiesDailyPerformance(state) {
    return state.propertiesDailyPerformance;
  },
  propertiesEnquiries(state) {
    return state.propertiesEnquiries;
  },
  propertiesEnquiriesLoaded(state) {
    return state.propertiesEnquiries.filter((a) => a.property.propertyId > 0).length > 0;
  },
  propertiesOverview(state) {
    const result: IPropertyOverview[] = [];

    state.propertiesPerformance.forEach((p) => {
        let enquiry = state.propertiesEnquiries.filter((a) => a.property.propertyId === p.property.propertyId)[0];

        if (!enquiry) {
            enquiry = {
                enquiries : 0,
                attempted: 0,
                contacted : 0,
                conversions : 0,
                revenue: 0,
                property : p.property
            };
        }

        result.push({
            performance: p,
            enquiry
        });
    });

    return result;
  },
  loadsCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.performance.loads).reduce((x, y) => x + y, 0);
  },
  impressionCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.performance.impressions).reduce((x, y) => x + y, 0);
  },
  clicksCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.performance.clicks).reduce((x, y) => x + y, 0);
  },
  keyPressesCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.performance.keyPresses).reduce((x, y) => x + y, 0);
  },
  enquiriesCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.enquiry.enquiries).reduce((x, y) => x + y, 0);
  },
  revenueCount(state, getters) {
    return getters.propertiesOverview.map((x) => x.enquiry.revenue).reduce((x, y) => x + y, 0);
  }
};

export const mutations: MutationTree<IPropertyOverviewState> = {
  propertiesPerformance(state, propertiesPerformance: IPropertyPerformanceSummary[]) {
    state.propertiesPerformance = propertiesPerformance;
  },
  propertiesDailyPerformance(state, data: IDailyPerformanceSummary) {
    state.propertiesDailyPerformance = data;
  },
  propertiesEnquiries(state, data: IPropertyEnquirySummary[]) {
    state.propertiesEnquiries = data;
  }
};

export const actions: ActionTree<IPropertyOverviewState, RootState> = {
  async retrievePropertiesPerformance({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getPropertiesPerformance(
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          commit("propertiesPerformance", value.result);
          resolve(value.success);
        });
    });
  },
  async retrievePropertiesDailyPerformance({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      container.get<PerformanceService>(PerformanceService)
        .getDailyPropertiesPerformance(
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          commit("propertiesDailyPerformance", value);
          resolve(true);
        });
    });
  },
  async retrievePropertiesEnquiries({ commit, rootState }) {
    return new Promise((resolve, reject) => {
      container.get<EnquiryService>(EnquiryService)
        .getEnquiries(
          rootState.userSelectionModule.dateFrom,
          rootState.userSelectionModule.dateTo)
        .then((value) => {
          commit("propertiesEnquiries", value.result);
          resolve(true);
        });
    });
  }
};

export const propertyOverviewModule: Module<IPropertyOverviewState, RootState> = {
  state, getters, mutations, actions, namespaced: true
};
